import { useQuery } from '@tanstack/react-query'
import { getAuditLog } from '../../services/api'
import { ACTION_LABELS, ACTION_COLORS, humaniseActionType } from '../../constants/activityLabels'
import type { AuditEntry } from '../../types'

interface AuditTimelineProps {
  documentId: string
}

function formatLabel(entry: AuditEntry): string {
  const base = ACTION_LABELS[entry.action_type] || humaniseActionType(entry.action_type)
  const meta = entry.metadata
  if (!meta) return base

  // Add contextual info from metadata
  if (entry.action_type === 'permission.grant' && meta.grantee_email) {
    return `Shared with ${meta.grantee_email}`
  }
  if (entry.action_type === 'document_list.add' && meta.title) {
    return `Saved: "${meta.title}"`
  }
  if (entry.action_type === 'document.download' && meta.user_email) {
    return `Downloaded by ${meta.user_email}`
  }

  return base
}

export default function AuditTimeline({ documentId }: AuditTimelineProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['audit', documentId],
    queryFn: () => getAuditLog({ resource_id: documentId, limit: 10 }),
    retry: 1,
  })

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3">
            <div className="w-3.5 h-3.5 rounded-full bg-gray-200 mt-0.5 animate-pulse" />
            <div className="flex-1 space-y-1">
              <div className="h-3 bg-gray-200 rounded w-3/4 animate-pulse" />
              <div className="h-2 bg-gray-100 rounded w-1/2 animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  const entries = data?.events?.filter((e: AuditEntry) => e.resource_id === documentId).slice(0, 10) || []

  if (!entries.length) {
    return <p className="text-xs text-gray-500">No activity recorded</p>
  }

  return (
    <div className="space-y-0 relative">
      {entries.map((entry, i) => {
        const color = ACTION_COLORS[entry.action_type] || 'bg-gray-400'
        return (
          <div key={entry.event_id || i} className="flex gap-3 relative">
            {i < entries.length - 1 && (
              <div className="absolute left-[6px] top-5 bottom-0 w-px bg-gray-100" />
            )}
            <div className={`w-3.5 h-3.5 rounded-full ${color} border-2 border-white mt-0.5 flex-shrink-0 relative z-10`} />
            <div className="pb-3">
              <p className="text-xs text-gray-700">{formatLabel(entry)}</p>
              <p className="text-xs text-gray-500">
                {new Date(entry.timestamp).toLocaleDateString('en-AU', {
                  day: '2-digit', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Sydney',
                })}
              </p>
            </div>
          </div>
        )
      })}
    </div>
  )
}
